/* eslint-disable @typescript-eslint/no-explicit-any */
// Frontend/lib/firebaseService.ts
// import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  collection,
  addDoc,
  getDocs,
  doc,
  setDoc,
  // updateDoc,
  getDoc,
  // deleteDoc,
  query,
  // where,
  orderBy,
  onSnapshot,
  Timestamp,
  DocumentData,
  QuerySnapshot,
  limit,
  writeBatch
} from 'firebase/firestore';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, Auth, signOut, User } from "firebase/auth";

import {
  ref,
  uploadBytes,
  getDownloadURL,
  // deleteObject
} from 'firebase/storage';

import { 
  getFirebaseDB,
  getFirebaseStorage,
  getFirebaseAuth as getFirebaseAuthClient,
  getFirebaseAI,
  getFirebaseModel,
  getFirebaseApp
} from './firebaseClient';

import { nanoid } from 'nanoid';
import { DocumentReference, Firestore } from 'firebase-admin/firestore';
import { AI } from 'firebase/ai';

interface FirebaseMessage {
  id?: string;
  content: string;
  accountID: string;
  timestamp: Timestamp;
  conversationId: string;
  sender: string;
  metadata?: any;
  isFile?: boolean;
  fileName?: string;
  originatingMessageId?: number;
}

interface ChatMessage {
  id: number;
  sender: string;
  text: string;
  time: string;
  isFile?: boolean;
  fileName?: string;
  originatingMessageId?: number;
}

interface FirebaseServiceConfig {
  defaultAccountID?: string;
  logger?: any;
  conversationID?: string
}

export class FirebaseService {
  private logger: any;
  private defaultAccountID?: string;
  private conversationID: string;

  constructor(config: FirebaseServiceConfig = {}) {
    this.logger = config.logger || console;
    this.defaultAccountID = config.defaultAccountID || process.env.DEFAULT_ACCOUNT_ID;
    this.conversationID = config.conversationID || nanoid();

    console.log('🔧 [FirebaseService] Constructor called');

    // Test Firebase initialization immediately
    setTimeout(async () => {
      console.log('🔧 [FirebaseService] Testing Firebase Auth access...');
      const auth = await this.getFirebaseAuth();
      if (auth) {
        console.log('✅ [FirebaseService] Firebase Auth ready');
      } else {
        console.log('❌ [FirebaseService] Firebase Auth not ready');
      }
    }, 1000);
  }

  public async getFirestore(): Promise<Firestore> {
    const db = await getFirebaseDB();
    return db;
  }

  setDefaultAccountID(accountID: string): void {
    this.defaultAccountID = accountID;
  }

  private async isFirebaseInitialized(): Promise<boolean> {
    try {
      console.log('🔍 Checking if Firebase is initialized...');
      const app = await getFirebaseApp();
      console.log('✅ Firebase app found:', app.name);
      return true;
    } catch (error) {
      console.log('❌ Firebase app not found:', error);
      return false;
    }
  }

  setConversationID(conversationID: string): void {
    this.conversationID = conversationID;
  }

  generateNewConversationID(): string {
    // Generate a new random conversation ID
    return Math.random().toString(36).substring(2) + Date.now().toString(36);
  }

  /**
   * Get Firebase Auth instance
   */
  async getFirebaseAuth(): Promise<Auth | null> {
    try {
      console.log('🔍 Attempting to get Firebase Auth...');
      const auth = await getFirebaseAuthClient();

      // Validate that this is actually a Firebase Auth instance
      if (auth && typeof auth.onAuthStateChanged === 'function') {
        console.log('✅ Firebase Auth obtained and validated successfully');
        return auth;
      } else {
        console.log('❌ Firebase Auth invalid - missing onAuthStateChanged method');
        return null;
      }
    } catch (error) {
      console.error('❌ Error getting Firebase Auth:', error);
      return null;
    }
  }

  /**
   * Get Firebase AI instance
   */
  public async getFirebaseAI(): Promise<AI> {
    try {
      console.log('🔍 Attempting to get Firebase AI...');
      const firebaseAI = await getFirebaseAI();
      if (!firebaseAI) {
        throw new Error('Firebase AI not initialized');
      }

      console.log('✅ Firebase AI obtained successfully');
      return firebaseAI;
    } catch (error) {
      console.error('❌ Error getting Firebase AI:', error);
      return null;
    }
  }

  /**
   * Get Firebase Generative Model instance
   */
  public async getGenerativeModel(): Promise<any> {
    try {
      console.log('🔍 Attempting to get Firebase Generative Model...');
      const ai = await getFirebaseAI();
      if (!ai) throw new Error('AI instance not available');

      const model = await getFirebaseModel();
      if (!model) throw new Error('Model instance not available');

      console.log('✅ Firebase Generative Model obtained successfully');
      return model;
    } catch (error) {
      console.error('❌ Error getting Firebase Generative Model:', error);
      return null;
    }
  }

  /**
   * Get the current user from Firebase Auth
   */
  async getCurrentUser(): Promise<User | null> {
    const auth = await this.getFirebaseAuth();
    if (!auth) return null;
    return auth.currentUser;
  }

  /**
 * Create a new user with email and password
 */
  async createUser(email: string, password: string): Promise<User> {
    const auth = await this.getFirebaseAuth();
    if (!auth) {
      throw new Error('Firebase Auth not initialized');
    }

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      this.logger.info('User created successfully:', userCredential.user.uid);
      return userCredential.user;
    } catch (error: any) {
      this.logger.error('Error creating user:', error.code, error.message);
      throw error;
    }
  }

  /**
   * Authenticate a user with email and password
   */
  async authenticateUser(email: string, password: string): Promise<User> {
    const auth = await this.getFirebaseAuth();
    if (!auth) {
      throw new Error('Firebase Auth not initialized');
    }

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      this.logger.info('User signed in successfully:', userCredential.user.uid);
      return userCredential.user;
    } catch (error: any) {
      this.logger.error('Error signing in user:', error.code, error.message);
      throw error;
    }
  }

  /**
   * Sign out the current user
   */
  async signOutUser(): Promise<void> {
    const auth = await this.getFirebaseAuth();
    if (!auth) {
      throw new Error('Firebase Auth not initialized');
    }

    try {
      await signOut(auth);
      this.logger.info('User signed out successfully');
    } catch (error: any) {
      this.logger.error('Error signing out user:', error);
      throw error;
    }
  }

  /**
   * Get the current conversation ID
   */
  getConversationID(): string {
    return this.conversationID;
  }

  /**
   * Check if a user is an admin
   * @param accountID - The account ID of the user to check
   * @returns true if the user is an admin, false otherwise
   */
  async checkUserAdminStatus(accountID: string): Promise<boolean> {
    try {
      if (!accountID) {
        throw new Error('Account ID is required to check admin status.');
      }
      this.logger.debug(`Checking admin status for account: ${accountID}`);

      // Get the Firestore instance
      const db = await getFirebaseDB();

      const userDocRef = doc(db, "Users", accountID);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        return userData?.isAdmin === true;
      }
      return false;
    } catch (error) {
      this.logger.error('Error checking admin status:', error);
      return false;
    }
  }

  async getFirebaseDocument(documentPath: DocumentReference): Promise<DocumentData> {
    try {

      // Get the Firestore instance
      const db = await getFirebaseDB();
      const docRef = doc(db, documentPath.path);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return docSnap.data();
      } else {
        throw new Error('Document not found');
      }
    } catch (error) {
      this.logger.error('Error retrieving Firebase Document:', error);
      throw error;
    }
  }


    /**
   * Check if a user is an admin
   * @param accountID - The account ID of the user to check
   * @returns the entire user document DocumentData object using the accountID string for context
   */
  async getUserDocument(accountID: string): Promise<DocumentData> {
    try {
      if (!accountID) {
        throw new Error('Account ID is required to check admin status.');
      }
      this.logger.debug(`Retrieving User Document status for account: ${accountID}`);

      // Get the Firestore instance
      const db = await getFirebaseDB();

      const userDocRef = doc(db, "Users", accountID);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        return userData;
      }
      return;
    } catch (error) {
      this.logger.error('Error checking admin status:', error);
      return;
    }
  }

  async getFirebaseStorage() {
    return await getFirebaseStorage();
  }

  async getFirebaseFirestore() {
    return await getFirebaseDB();
  }

  /**
   * Save local dev URL to user document using Admin SDK
   */
  async setLocalDevURL(
    content: string,
    accountID: string,
    metadata?: any
  ): Promise<string> {
    try {
      const resolvedAccountID = this.getAccountID(accountID);
      // Get the Firestore instance
      const db = await getFirebaseDB();
      const userDocRef = doc(db, "Users", resolvedAccountID);

      // set() doesn't return a document reference with an ID, it returns void
      await setDoc(userDocRef, {
        localDevURL: content,
        localDevURLTimestamp: Timestamp.now(),
        ...metadata // Spread any additional metadata
      }, { merge: true });

      this.logger.info(`Local Dev URL set for account: ${resolvedAccountID}`);
      this.logger.info(`Local Dev URL set with content: ${content} for account: ${resolvedAccountID}`);

      // Return the account ID since that's the document ID
      return resolvedAccountID;
    } catch (error) {
      this.logger.error('Error saving local dev URL:', error);
      throw error;
    }
  }

  async setGAIAAgentDevURL(
    content: string,
    accountID: string,
    metadata?: any
  ): Promise<string> {
    try {
      const resolvedAccountID = this.getAccountID(accountID);
      // Get the Firestore instance
      const db = await getFirebaseDB();
      const userDocRef = doc(db, "Users", resolvedAccountID);

      // set() doesn't return a document reference with an ID, it returns void
      await setDoc(userDocRef, {
        gaiaAgentDevURL: content,
        GAIAAgentDevURLTimestamp: Timestamp.now(),
        ...metadata // Spread any additional metadata
      }, { merge: true });

      this.logger.info(`GAIA Agent Dev URL set for account: ${resolvedAccountID}`);
      this.logger.info(`GAIA Agent Dev URL set with content: ${content} for account: ${resolvedAccountID}`);

      // Return the account ID since that's the document ID
      return resolvedAccountID;
    } catch (error) {
      this.logger.error('Error saving local dev URL:', error);
      throw error;
    }
  }

  /**
  * Get local dev URL from user document
  */
  async getLocalDevURL(accountID: string): Promise<string | null> {
    try {
      const resolvedAccountID = this.getAccountID(accountID);
      // Get the Firestore instance
      const db = await getFirebaseDB();
      const userDoc = await getDoc(doc(db, "Users", resolvedAccountID));

      if (userDoc.exists()) {
        const userData = userDoc.data();
        return userData?.localDevURL || null;
      }

      return null;
    } catch (error) {
      this.logger.error('Error getting local dev URL:', error);
      return null;
    }
  }

  /**
   * Get account ID - either from parameter or default
   */
  private getAccountID(accountID?: string): string {
    const resolvedAccountID = accountID || this.defaultAccountID;

    if (!resolvedAccountID) {
      throw new Error('Account ID is required. Please provide accountID parameter or set DEFAULT_ACCOUNT_ID environment variable.');
    }

    return resolvedAccountID;
  }

  /**
 * Get list of conversation IDs for a user
 */
  async getConversationList(accountID?: string): Promise<string[]> {
    try {
      const db = await getFirebaseDB();
      const resolvedAccountID = this.getAccountID(accountID);

      this.logger.debug(`Fetching conversation list for account: ${resolvedAccountID}`);

      const userDocRef = doc(db, "Users", resolvedAccountID);
      const conversationsCollectionRef = collection(userDocRef, "Conversations");

      // Query conversations ordered by last update (most recent first)
      const q = query(conversationsCollectionRef, orderBy('updatedAt', 'desc'));
      const querySnapshot = await getDocs(q);

      const conversationIds = querySnapshot.docs.map(doc => doc.id);

      this.logger.info(`Retrieved ${conversationIds.length} conversations for account: ${resolvedAccountID}`);
      return conversationIds;
    } catch (error) {
      this.logger.error('Error fetching conversation list:', error);
      throw error;
    }
  }

  /**
   * Get conversation metadata (title, last message, etc.)
   */
  async getConversationMetadata(
    conversationId: string,
    accountID?: string
  ): Promise<any> {
    try {
      const db = await getFirebaseDB();
      const resolvedAccountID = this.getAccountID(accountID);

      this.logger.debug(`Fetching conversation metadata for: ${conversationId}, account: ${resolvedAccountID}`);

      const userDocRef = doc(db, "Users", resolvedAccountID);
      const conversationDocRef = doc(userDocRef, "Conversations", conversationId);

      const conversationDoc = await getDoc(conversationDocRef);

      if (!conversationDoc.exists()) {
        this.logger.warn(`Conversation ${conversationId} not found for account ${resolvedAccountID}`);
        return null;
      }

      const data = conversationDoc.data();
      return {
        conversationId,
        lastMessage: data?.lastMessage || '',
        lastMessageTimestamp: data?.lastMessageTimestamp?.toDate() || null,
        lastMessageSender: data?.lastMessageSender || '',
        createdAt: data?.createdAt?.toDate() || null,
        updatedAt: data?.updatedAt?.toDate() || null,
        participantIDs: data?.participantIDs || [],
        title: data?.title || '', // Include title if it exists
      };
    } catch (error) {
      this.logger.error('Error fetching conversation metadata:', error);
      throw error;
    }
  }

  // Frontend/lib/firebaseService.ts
  private chatMessageToFirebase(
    chatMessage: ChatMessage,
    accountID: string,
    conversationId: string
  ): Omit<FirebaseMessage, 'id' | 'timestamp'> {
    // Only include defined values in metadata
    const metadata: any = {
      originalId: chatMessage.id,
      originalTime: chatMessage.time
    };

    // Only add isFile and fileName if they're defined
    if (chatMessage.isFile !== undefined) {
      metadata.isFile = chatMessage.isFile;
    }
    if (chatMessage.fileName !== undefined) {
      metadata.fileName = chatMessage.fileName;
    }

    return {
      content: chatMessage.text,
      accountID,
      conversationId,

      // ✅ FIXED: Preserve the original sender name exactly as provided
      sender: chatMessage.sender, // Don't modify the sender
      originatingMessageId: chatMessage.id,
      metadata
    };
  }

  // Fixed firebaseMessageToChat method that preserves original sender names
  private firebaseMessageToChat(firebaseMessage: FirebaseMessage): ChatMessage {
    return {
      id: firebaseMessage.metadata?.originalId || Date.now(),
      originatingMessageId: firebaseMessage.originatingMessageId || Date.now(),
      // ✅ FIXED: Preserve the original sender from Firebase
      sender: firebaseMessage.sender, // Keep exact sender from Firebase

      text: firebaseMessage.content,
      time: firebaseMessage.metadata?.originalTime ||
        firebaseMessage.timestamp.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      // Only set these if they exist in metadata
      ...(firebaseMessage.metadata?.isFile !== undefined && { isFile: firebaseMessage.metadata.isFile }),
      ...(firebaseMessage.metadata?.fileName !== undefined && { fileName: firebaseMessage.metadata.fileName })
    };
  }

  async saveProfileData(profileData:any) {
    const db = await getFirebaseDB();
    const resolvedAccountID = this.getAccountID(profileData.accountID);
    this.logger.debug(`Saving Profile Data to Firebase for account: ${resolvedAccountID}`);

    const userDocRef = doc(db, "Users", resolvedAccountID);


    console.log("Updating Account Profile Data:", profileData, "\nPath:", userDocRef.path);

    await setDoc(userDocRef, {
      ...profileData,
      timestamp: Timestamp.now(),
      createdAt: Timestamp.now(),
    }, { merge: true });

    this.logger.info(`Account Profile Data saved successfully!`, "\nPath:", userDocRef.path);
    return userDocRef.path;
  }

  /**
   * Save a chat message to Firebase
   */
  async saveChatMessage(
    chatMessage: ChatMessage,
    conversationId?: string,
    accountID?: string
  ): Promise<string> {
    try {
      const db = await getFirebaseDB();
      const resolvedAccountID = this.getAccountID(accountID);
      const resolvedConversationId = conversationId || this.conversationID;

      this.logger.debug(`Saving chat message to Firebase for account: ${resolvedAccountID}, conversation: ${resolvedConversationId}`);

      const firebaseMessage = this.chatMessageToFirebase(chatMessage, resolvedAccountID, resolvedConversationId);

      const userDocRef = doc(db, "Users", resolvedAccountID);
      const userConversationDocRef = doc(userDocRef, "Conversations", resolvedConversationId);
      const userConversationMessagesCollectionRef = collection(userConversationDocRef, "Messages");

      console.log("Adding Message to firebase:", firebaseMessage, "\nPath:", userConversationMessagesCollectionRef.path);

      const messageDocRef = await addDoc(userConversationMessagesCollectionRef, {
        ...firebaseMessage,
        timestamp: Timestamp.now()
      });

      await setDoc(userConversationDocRef, {
        lastMessage: firebaseMessage.content,
        lastMessageTimestamp: Timestamp.now(),
        lastMessageSender: firebaseMessage.sender,
        participantIDs: [resolvedAccountID],
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      }, { merge: true });

      this.logger.info(`Chat message saved with ID: ${messageDocRef.id} in conversation: ${resolvedConversationId}`);
      return messageDocRef.id;
    } catch (error) {
      this.logger.error('Error saving chat message:', error);
      throw error;
    }
  }

  /**
   * Get chat messages for a conversation
   */
  async getChatMessages(
    conversationId?: string,
    accountID?: string,
    limitCount?: number,
  ): Promise<ChatMessage[]> {
    try {
      const db = await getFirebaseDB();
      const resolvedAccountID = this.getAccountID(accountID);
      const resolvedConversationId = conversationId || this.conversationID;

      this.logger.debug(`Fetching chat messages for conversation: ${resolvedConversationId}, account: ${resolvedAccountID}`);

      const userDocRef = doc(db, "Users", resolvedAccountID);
      const userConversationDocRef = doc(userDocRef, "Conversations", resolvedConversationId);
      const messagesCollectionRef = collection(userConversationDocRef, "Messages");

      // Build query with optional limit
      let q = query(messagesCollectionRef, orderBy('timestamp', 'asc'));

      if (limitCount && limitCount > 0) {
        // For recent messages, we want to order by timestamp desc, limit, then reverse
        q = query(messagesCollectionRef, orderBy('timestamp', 'desc'), limit(limitCount));
      }

      const querySnapshot = await getDocs(q);

      let firebaseMessages = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as FirebaseMessage[];

      // If we used desc order with limit, reverse to get chronological order
      if (limitCount && limitCount > 0) {
        firebaseMessages = firebaseMessages.reverse();
      }

      const chatMessages = firebaseMessages.map(msg => this.firebaseMessageToChat(msg));

      this.logger.info(`Retrieved ${chatMessages.length} chat messages`);
      return chatMessages;
    } catch (error) {
      this.logger.error('Error fetching chat messages:', error);
      throw error;
    }
  }

  /**
   * Delete a conversation and all its messages
   */
  async deleteConversation(
    conversationId: string,
    accountID?: string
  ): Promise<void> {
    try {
      const db = await getFirebaseDB();
      const resolvedAccountID = this.getAccountID(accountID);

      this.logger.debug(`Deleting conversation: ${conversationId} for account: ${resolvedAccountID}`);

      const userDocRef = doc(db, "Users", resolvedAccountID);
      const conversationDocRef = doc(userDocRef, "Conversations", conversationId);
      const messagesCollectionRef = collection(conversationDocRef, "Messages");

      // Delete all messages first
      const messagesSnapshot = await getDocs(messagesCollectionRef);
      const batch = writeBatch(db);

      messagesSnapshot.docs.forEach(doc => {
        batch.delete(doc.ref);
      });

      // Delete the conversation document
      batch.delete(conversationDocRef);

      await batch.commit();

      this.logger.info(`Conversation ${conversationId} deleted successfully`);
    } catch (error) {
      this.logger.error('Error deleting conversation:', error);
      throw error;
    }
  }

  /**
   * Check if a conversation exists
   */
  async conversationExists(
    conversationId: string,
    accountID?: string
  ): Promise<boolean> {
    try {
      const db = await getFirebaseDB();
      const resolvedAccountID = this.getAccountID(accountID);

      const userDocRef = doc(db, "Users", resolvedAccountID);
      const conversationDocRef = doc(userDocRef, "Conversations", conversationId);

      const conversationDoc = await getDoc(conversationDocRef);
      return conversationDoc.exists();
    } catch (error) {
      this.logger.error('Error checking if conversation exists:', error);
      return false;
    }
  }

  /**
   * Get conversation count for a user
   */
  async getConversationCount(accountID?: string): Promise<number> {
    try {
      const conversationIds = await this.getConversationList(accountID);
      return conversationIds.length;
    } catch (error) {
      this.logger.error('Error getting conversation count:', error);
      return 0;
    }
  }

  /**
   * Set conversation metadata (title, custom properties, etc.)
   */
  async updateConversationMetadata(
    conversationId: string,
    metadata: { [key: string]: any },
    accountID?: string
  ): Promise<void> {
    try {
      const db = await getFirebaseDB();
      const resolvedAccountID = this.getAccountID(accountID);

      this.logger.debug(`Updating conversation metadata for: ${conversationId}, account: ${resolvedAccountID}`);

      const userDocRef = doc(db, "Users", resolvedAccountID);
      const conversationDocRef = doc(userDocRef, "Conversations", conversationId);

      await setDoc(conversationDocRef, {
        ...metadata,
        updatedAt: Timestamp.now()
      }, { merge: true });

      this.logger.info(`Conversation metadata updated for: ${conversationId}`);
    } catch (error) {
      this.logger.error('Error updating conversation metadata:', error);
      throw error;
    }
  }

  /**
   * Subscribe to real-time chat message updates
   */
  async subscribeToChatMessages(
    conversationId: string,
    callback: (messages: ChatMessage[]) => void,
    accountID?: string
  ): Promise<() => void> {
    const db = await getFirebaseDB();
    const resolvedAccountID = this.getAccountID(accountID);
    const resolvedConversationId = conversationId || this.conversationID;

    this.logger.debug(`Subscribing to chat messages for conversation: ${resolvedConversationId}, account: ${resolvedAccountID}`);

    const userDocRef = doc(db, "Users", resolvedAccountID);
    const userConversationDocRef = doc(userDocRef, "Conversations", resolvedConversationId);
    const messagesCollectionRef = collection(userConversationDocRef, "Messages");

    const q = query(messagesCollectionRef, orderBy('timestamp', 'asc'));

    const unsubscribe = onSnapshot(q, (querySnapshot: QuerySnapshot<DocumentData>) => {
      const firebaseMessages = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as FirebaseMessage[];

      const chatMessages = firebaseMessages.map(msg => this.firebaseMessageToChat(msg));
      callback(chatMessages);
    });

    return unsubscribe;
  }

  /**
   * Upload file to Firebase Storage
   */
  async uploadFile(
    file: File,
    path: string,
    accountID?: string
  ): Promise<string> {
    try {
      const storage = await getFirebaseStorage();
      const resolvedAccountID = this.getAccountID(accountID);
      const fullPath = `Users/${resolvedAccountID}/${path}`;

      this.logger.debug(`Uploading file: ${file.name} to ${fullPath}`);

      const storageRef = ref(storage, fullPath);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);

      this.logger.info(`File uploaded successfully: ${downloadURL}`);
      return downloadURL;
    } catch (error) {
      this.logger.error('Error uploading file:', error);
      throw error;
    }
  }
}